import GenericCryptoTradingView from "./Charts/GenericCryptoTradingView";
import ProTradingView from "./Charts/ProTradingView";
import CityTradingView from "./Charts/CityTradingView";
import PokemonTradingView from "./Charts/PokemonTradingView";
import WatchTradingView from "./Charts/WatchTradingView";
import PocTradingView from "./Charts/PocTradingView";
import { useState, useEffect, useRef, useCallback } from "react";
import { getMarketsByCategory, MARKET_CATEGORIES, getMarketById } from "../../config/markets";
import { getTradingViewSymbol } from "../../utils/tradingViewSymbols";
import useIsMobile from "../../hooks/useIsMobile";

// Skeleton loader that renders INSIDE the TradingChart container
// Matches the inner structure of CityTradingView/PokemonTradingView exactly
const ChartSkeleton = ({ isMobile }) => {
  if (isMobile) {
    return (
      <div className="h-80 animate-pulse">
        <div className="h-full rounded-[14px] mx-4 mt-4" style={{ background: 'var(--surface-3)' }}></div>
      </div>
    );
  }

  // Desktop: matches the inner layout of CityTradingView/PokemonTradingView.
  // Skeleton blocks are `--surface-3` pills — the shape a thing is *going* to
  // be, not a grey rectangle.
  return (
    <div className="w-full p-6 animate-pulse" style={{ background: 'transparent' }}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="h-6 rounded-full w-48 mb-2" style={{ background: 'var(--surface-3)' }}></div>
          <div className="h-4 rounded-full w-64" style={{ background: 'var(--surface-3)' }}></div>
        </div>
        <div className="h-10 rounded-full w-20" style={{ background: 'var(--surface-3)' }}></div>
      </div>
      <div className="h-96 rounded-[14px]" style={{ background: 'var(--surface-3)' }}></div>
    </div>
  );
};

const TradingChart = ({ chartType }) => {
  const isMobile = useIsMobile();
  const [selectedInterval, setSelectedInterval] = useState('1M');
  const [isContainerReady, setIsContainerReady] = useState(false);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [isChartLoading, setIsChartLoading] = useState(true);
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  
  const cityDisplayNames = {
    "dubai": "Dubai",
    "los-angeles": "Los Angeles",
    "new-york": "New York",
    "chicago": "Chicago",
    "washington": "Washington",
    "pittsburgh": "Pittsburgh",
    "miami-beach": "Miami Beach",
    "boston": "Boston",
    "brooklyn": "Brooklyn",
    "austin": "Austin",
    "denver": "Denver"
  };

  // Monitor container dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setContainerDimensions({ width, height });
        
        // Only set ready when we have actual dimensions
        if (width > 0 && height > 0) {
          setIsContainerReady(true);
        }
      }
    };

    // Initial check
    updateDimensions();

    // Set up ResizeObserver for container size changes
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also check after a delay to ensure DOM is ready
    const timer = setTimeout(updateDimensions, 200);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(timer);
    };
  }, []);

  // Reset loading state when chart type changes
  useEffect(() => {
    setIsChartLoading(true);
    // Show skeleton for 1.5 seconds
    const timer = setTimeout(() => {
      setIsChartLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [chartType]);

  // Handle interval change for mobile - just update state, no forced re-render
  const handleIntervalChange = useCallback((interval) => {
    setSelectedInterval(interval);
  }, []);

  const renderChart = () => {
    if (!isContainerReady || containerDimensions.width === 0) {
      return (
        <div className="w-full h-80 flex items-center justify-center">
          <div className="animate-pulse text-gray-500">Loading chart...</div>
        </div>
      );
    }

    // Determine if this is a city chart
    const isCityChart = cityDisplayNames.hasOwnProperty(chartType);
    
    // Check if this is POKE-INDEX chart
    const isPokemonChart = chartType === 'poke' || chartType === 'pokemon';

    // Check if this is a Watch chart
    const isWatchChart = chartType?.startsWith('watch-');

    // Check if this is a PoC chart
    const isPocChart = chartType?.startsWith('poc-');

    // PSA-10 is a live market (tradeable price from the oracle) whose chart
    // history/24h badge come from Perps-Server's psa10candles collection
    // (daily + hourly granularity) rather than the oracle, so it gets its own
    // server-backed chart branch below instead of the on-chain ProTradingView.
    const isPsa10Chart = chartType === 'psa10';

    // Show skeleton for real estate / Pokemon / Watch / PoC / PSA-10 charts while loading
    if ((isCityChart || isPokemonChart || isWatchChart || isPocChart || isPsa10Chart) && isChartLoading) {
      return <ChartSkeleton isMobile={isMobile} />;
    }

    // Use selectedInterval directly for city charts, Pokemon, and Watch (no mapping needed)
    const chartInterval = isMobile ? selectedInterval : undefined;

    // Pokemon chart - use PokemonTradingView component
    if (isPokemonChart) {
      return (
        <PokemonTradingView
          initialInterval={isMobile ? chartInterval : undefined}
        />
      );
    }

    // Watch chart - use WatchTradingView component
    if (isWatchChart) {
      // Extract brandKey from chartType (e.g., 'watch-rolex' -> market lookup -> watchBrandKey)
      const watchMarket = [
        ...getMarketsByCategory(MARKET_CATEGORIES.WATCHES),
        ...getMarketsByCategory(MARKET_CATEGORIES.RWA),
      ].find(m => m.chartType === chartType);
      const brandKey = watchMarket?.watchBrandKey || 'ALL_WATCHES';
      return (
        <WatchTradingView
          brandKey={brandKey}
          initialInterval={isMobile ? chartInterval : undefined}
        />
      );
    }
    
    // PSA-10 chart - server-backed index history from /psa10/chart-data.
    // enableHourly reads the dedicated /psa10/hourly-history endpoint
    // (psa10candles '1h' granularity) for the default 24H view — no other
    // market has hourly data today.
    if (isPsa10Chart) {
      return (
        <PocTradingView
          endpoint="psa10/chart-data"
          displayName="PSA 10"
          initialInterval={isMobile ? chartInterval : undefined}
          enableHourly
          defaultInterval="1H"
        />
      );
    }

    // PoC chart - use PocTradingView component
    if (isPocChart) {
      const pocMarket = [...getMarketsByCategory(MARKET_CATEGORIES.STOCKS), ...getMarketsByCategory(MARKET_CATEGORIES.COMMODITIES), ...getMarketsByCategory(MARKET_CATEGORIES.RWA), ...getMarketsByCategory(MARKET_CATEGORIES.CRYPTO)]
        .find(m => m.chartType === chartType);
      const pocMarketKey = pocMarket?.pocMarketKey || chartType.replace('poc-', '');
      return (
        <PocTradingView
          marketKey={pocMarketKey}
          displayName={pocMarket?.name || chartType.replace('poc-', '')}
          initialInterval={isMobile ? chartInterval : undefined}
        />
      );
    }

    // Check if this is a stock market — use ProTradingView with full toolbars
    const stockMarkets = getMarketsByCategory(MARKET_CATEGORIES.STOCKS);
    const stockMarket = stockMarkets.find(m => m.chartType === chartType || m.id === chartType);
    if (stockMarket && stockMarket.tradingViewSymbol) {
      return (
        <ProTradingView
          symbol={stockMarket.tradingViewSymbol}
          defaultInterval="1"
        />
      );
    }

    // Check if this is a commodity market — use ProTradingView with full toolbars
    const commodityMarkets = getMarketsByCategory(MARKET_CATEGORIES.COMMODITIES);
    const commodityMarket = commodityMarkets.find(m => m.chartType === chartType || m.id === chartType);
    if (commodityMarket && commodityMarket.tradingViewSymbol) {
      return (
        <ProTradingView
          symbol={commodityMarket.tradingViewSymbol}
          defaultInterval="D"
        />
      );
    }

    // Check if this is a crypto market
    const cryptoMarkets = getMarketsByCategory(MARKET_CATEGORIES.CRYPTO);
    const cryptoMarket = cryptoMarkets.find(m => m.chartType === chartType || m.id === chartType);

    if (cryptoMarket && cryptoMarket.tradingViewSymbol) {
      return (
        <ProTradingView
          symbol={cryptoMarket.tradingViewSymbol}
          defaultInterval="1H"
        />
      );
    }
    
    // City charts - all use the same component with city prop
    if (isCityChart) {
      return (
        <CityTradingView 
          city={chartType} 
          initialInterval={isMobile ? chartInterval : undefined}
        />
      );
    }
    
    // Fallback: Try to get symbol from utility function
    const fallbackSymbol = getTradingViewSymbol(chartType, chartType);
    if (fallbackSymbol) {
      return (
        <ProTradingView
          symbol={fallbackSymbol}
          defaultInterval="1H"
        />
      );
    }
    
    // Final fallback to Dubai if chartType is not recognized
    return (
      <CityTradingView 
        city="dubai" 
        initialInterval={isMobile ? chartInterval : undefined}
      />
    );
  };

  // Ranges offered by the mobile rail. PSA-10 is the one market with hourly
  // candles (PocTradingView's `enableHourly`), so it gets the extra 24H step —
  // same value/label pair the chart's own desktop selector uses.
  const mobileIntervals = [
    ...(chartType === 'psa10' ? [{ value: '1H', label: '24H' }] : []),
    ...['1W', '1M', '3M', '6M', '1Y', 'ALL'].map((v) => ({ value: v, label: v })),
  ];

  if (isMobile) {
    // Mobile View
    return (
      <div className="bg-white">
        <div
          ref={containerRef}
          className="w-full"
          style={{ minHeight: '340px' }}
        >
          {renderChart()}
        </div>
        
        {/* Mobile Chart Controls - Only show for the server-backed charts that
            read `initialInterval` (city, Pokemon, PSA-10, Watch, PoC), and when
            not loading. PSA-10 was missing from this list, so on mobile it
            rendered the chart with no way to change the range at all — the
            desktop selector inside the chart is suppressed whenever the parent
            controls the interval. */}
        {(cityDisplayNames.hasOwnProperty(chartType) || chartType === 'poke' || chartType === 'pokemon' || chartType === 'psa10' || chartType?.startsWith('watch-') || chartType?.startsWith('poc-')) && !isChartLoading && (
          <div className="px-4 py-3 bg-white -mt-4">
            {/* A sunken rail holding one raised interval — the same segmented
                object as the header nav and the table's tabs. Never two fills
                that swap. */}
            <div className="flex justify-center items-center">
              <div className="ce-seg">
                {mobileIntervals.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleIntervalChange(value)}
                    data-active={selectedInterval === value}
                    className={mobileIntervals.length > 6 ? 'ce-seg-item px-2' : 'ce-seg-item px-3'}
                    style={selectedInterval === value
                      ? { background: 'var(--raise)', boxShadow: 'var(--pillow-sm)', color: 'var(--cat-accent)' }
                      : undefined}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop View
  return (
    <div
      className="ce-chart-card mt-4 w-full"
      style={{
        background: "var(--surface)",
        borderRadius: 18,
        padding: "12px 14px 10px",
        // A card gets `--card` and no border — the shadow is the edge.
        boxShadow: "var(--card)",
      }}
    >
      <div className="w-full flex justify-end" />
      {/* The chart area is *recessed*, not a sticker sitting on the card. This
          is the single most legible change on the page: the series reads as
          something happening inside a well rather than a graphic pasted on
          top. `overflow: hidden` is what keeps the well's corners. */}
      <div
        ref={containerRef}
        className="ce-chart-well w-full h-full"
        style={{
          minHeight: '350px',
          borderRadius: 14,
          background: 'var(--surface-2)',
          boxShadow: 'var(--inset-sm)',
          overflow: 'hidden',
        }}
      >
        {renderChart()}
      </div>
    </div>
  );
};

export default TradingChart;